import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { AuthApi } from 'src/app/shared/apis/auth.api';
import { CSButtonComponent } from 'src/app/shared/components/cs-button/cs-button.component';
import { CSIconButtonComponent } from 'src/app/shared/components/cs-icon-button/cs-icon-button.component';
import {
  QuickSendEmailParams,
  QuickSendMobileVerifyParams,
  SendAction,
  SendEmailVerfiyParams,
  SendMobileVerifyParams,
  VerifyAction,
} from 'src/app/shared/interfaces/auth.interface';
import { ResponseData } from 'src/app/shared/interfaces/response.interface';
import { MaskEmailPipe } from 'src/app/shared/pipes/maskEmail.pipe';
import { GeetestService } from 'src/app/shared/service/geetest.service';
import { LocaleService } from 'src/app/shared/service/locale.service';
import { ToastService } from 'src/app/shared/service/toast.service';
import { AccountApi } from '../../apis/account.api';
import {
  AccountInforData,
  VerifyBindEmailForUpdateParams,
  VerifyBindEmailParams,
  VerifyBindMobileForUpdateParams,
  VerifyBindMobileParams,
} from '../../interfaces/account.interface';
import { CsConfirmPopupComponent } from '../cs-popup/cs-confirm-popup/cs-confirm-popup.component';

const countdownTime = 60;

@Component({
  standalone: true,
  selector: 'verify-2fa',
  templateUrl: './verify-2fa.component.html',
  styleUrls: ['./verify-2fa.component.scss'],
  imports: [
    CSIconButtonComponent,
    CSButtonComponent,
    CommonModule,
    FormsModule,
    MaskEmailPipe,
    CsConfirmPopupComponent,
  ],
})
export class Verify2FAComponent implements OnInit {
  constructor(
    public dialogRef: MatDialogRef<Verify2FAComponent>,
    private geetest: GeetestService,
    private toast: ToastService,
    private localeService: LocaleService,
    private authApi: AuthApi,
    private appService: AppService,
    private accountApi: AccountApi,
    private cdr: ChangeDetectorRef,
    private router: Router,
  ) {
    // 禁止點擊背景關閉對話框
    this.dialogRef.disableClose = true;
  }

  // 是否為指定的驗證類型; 一般2FA驗證為 false, 特定類型如綁定郵箱、更新郵箱等為 true
  @Input() isSpecificType = false;

  @Input() header = '';

  @Input() title = '';

  @Input() description = '';

  @Input() email = '';

  @Input() mobile = '';

  @Input() areaCode = '';

  @Input() otpType: VerifyAction = 'Login';

  // sendType:用於綁定或更新郵箱的操作類型; null: 一般2FA驗證
  @Input() sendType: SendAction = 'Email';

  @Input() uniCode: string | null = null;

  @Input() submitText = 'Submit';

  @Input() submitFunc: ((codes: string, verifyType?: 'EMAIL' | 'GOOGLE' | 'MOBILE') => Promise<boolean>) | null = null;

  @Input() verifyFunc: ((data: ResponseData<string>) => Promise<boolean>) | null = null;

  //如果希望執行 general2faVerify 可以接收這個參數;但注意不要與 submitFunc 同時使用
  @Output() verifyResponse = new EventEmitter();

  /** 发送验证码回傳 */
  @Output() sendOtpResponse = new EventEmitter();

  @Output() sendTypeChange = new EventEmitter<SendAction>();

  @ViewChildren('inputRef') inputs!: QueryList<ElementRef<HTMLInputElement>>;

  verifyType: 'EMAIL' | 'GOOGLE' | 'MOBILE' = 'EMAIL';

  codes = new Array(6).fill('');

  geetestData: {
    lotNumber: string;
    captchaOutput: string;
    passToken: string;
    genTime: string;
  } | null = null;

  userInfo?: AccountInforData;
  isSend = true;
  isCountingDown = false;
  countdown = countdownTime;
  countdownInterval: any = null;
  codeDisabled = false;
  errorCodes = new Array(6).fill('');
  isDirty = false;
  loading = false;
  userHas2fa = true;
  private hasFallbackToEmail = false;

  @HostListener('document:keydown.enter', ['$event'])
  public onKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !this.hasError) {
      this.submit();
    }
  }

  ngOnInit() {
    (async () => {
      try {
        this.loading = true;
        await this.handleVerifyTypeBySendType();

        if (this.userHas2fa) {
          await this.send2fa();
        }
      } catch (err) {
        console.error(err);
      } finally {
        this.loading = false; // 一定會執行
        setTimeout(() => this.focusFirstInput());
      }
    })();
  }

  focusFirstInput() {
    const firstInput = this.inputs.first;
    if (firstInput) {
      firstInput.nativeElement.focus();
    }
  }

  async handleVerifyTypeBySendType() {
    if (!this.isSpecificType) {
      this.userInfo = (await firstValueFrom(this.appService.userInfo$)) || undefined;
      const defaultOtpMethod = this.userInfo?.userSetting?.defaultOtpMethod ?? 'None';
      switch (defaultOtpMethod) {
        case 'Email':
          this.sendType = 'AuthEmail';
          this.email = this.userInfo?.email || '';
          break;
        case 'Mobile':
          this.sendType = 'AuthMobile';
          this.mobile = this.userInfo?.mobile || '';
          this.areaCode = this.userInfo?.areaCode || '';
          break;
        case 'Google':
          this.sendType = 'AuthGoogle';
          break;
        case 'None':
        default:
          this.userHas2fa = false;
          break;
      }
    }
    switch (this.sendType) {
      case 'BindEmail':
      case 'UpdateEmail':
      case 'AuthEmail':
        this.verifyType = 'EMAIL';
        break;
      case 'BindMobile':
      case 'UpdateMobile':
      case 'Mobile':
      case 'AuthMobile':
        this.verifyType = 'MOBILE';
        break;
      case 'AuthGoogle':
        this.verifyType = 'GOOGLE';
        break;
      default:
        this.verifyType = 'EMAIL';
        break;
    }
  }

  onInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.replace(/[^0-9]/g, '');
    const caretPos = input.selectionStart ?? value.length;
    const digit = value.charAt(caretPos - 1) || value.charAt(value.length - 1);

    input.value = digit;
    this.codes[index] = digit;

    if (!digit) return; // 沒輸入數字就不動

    if (index < this.codes.length - 1) {
      this.inputs.toArray()[index + 1].nativeElement.focus();
    } else if (index === this.codes.length - 1) {
      this.submit();
    }
  }

  onKeyDown(event: KeyboardEvent, index: number): void {
    const input = event.target as HTMLInputElement;

    // Backspace：本格為空時跳回並清除前一格
    if (event.key === 'Backspace' && !input.value && index > 0) {
      const prevInput = this.inputs.toArray()[index - 1].nativeElement;
      prevInput.value = '';
      this.codes[index - 1] = '';
      prevInput.focus();
      event.preventDefault();
    }

    // 左右方向鍵移動（可選）
    if (event.key === 'ArrowLeft' && index > 0) {
      this.inputs.toArray()[index - 1].nativeElement.focus();
      event.preventDefault();
    }
    if (event.key === 'ArrowRight' && index < this.codes.length - 1) {
      this.inputs.toArray()[index + 1].nativeElement.focus();
      event.preventDefault();
    }
  }

  onPaste(event: ClipboardEvent): void {
    const pasteData = event.clipboardData?.getData('text') || '';
    const digits = pasteData.replace(/\D/g, '').slice(0, 6).split('');

    this.inputs.forEach((input, i) => {
      if (i < digits.length) {
        input.nativeElement.value = digits[i];
        this.codes[i] = digits[i];
      } else {
        input.nativeElement.value = '';
        this.codes[i] = '';
      }
    });

    const codes = this.inputs.map(input => input.nativeElement.value);

    if (codes.length === 6) {
      this.submit();
    }

    event.preventDefault();
  }

  async send2fa() {
    try {
      const result = await firstValueFrom(await this.geetest.verify());
      this.geetestData = {
        lotNumber: result?.lot_number || '',
        captchaOutput: result?.captcha_output || '',
        passToken: result?.pass_token || '',
        genTime: result?.gen_time || '',
      };

      if (!result) return;

      if (this.sendType === 'Email') {
        const param: SendEmailVerfiyParams = {
          lotNumber: result.lot_number,
          captchaOutput: result.captcha_output,
          passToken: result.pass_token,
          genTime: result.gen_time,
          email: this.email,
          otpType: this.otpType,
        };
        const callbackData = await firstValueFrom(this.authApi.sendEmailVerify(param));
        this.onProcessEmailResponse(callbackData);
      } else if (this.sendType === 'BindEmail') {
        const param: VerifyBindEmailParams = {
          lotNumber: result.lot_number,
          captchaOutput: result.captcha_output,
          passToken: result.pass_token,
          genTime: result.gen_time,
          email: this.email,
          otpType: this.otpType,
        };
        const callbackData = await firstValueFrom(this.accountApi.pubsVerifyBindEmail(param));
        this.onProcessEmailResponse(callbackData);
      } else if (this.sendType === 'AuthEmail') {
        const param: QuickSendEmailParams = {
          lotNumber: result.lot_number,
          captchaOutput: result.captcha_output,
          passToken: result.pass_token,
          genTime: result.gen_time,
          otpType: this.otpType,
        };
        const callbackData = await firstValueFrom(this.authApi.quickSendEmailVerify(param));
        this.onProcessEmailResponse(callbackData);
      } else if (this.sendType === 'UpdateEmail') {
        const param: VerifyBindEmailForUpdateParams = {
          lotNumber: result.lot_number,
          captchaOutput: result.captcha_output,
          passToken: result.pass_token,
          genTime: result.gen_time,
          email: this.email,
          otpType: this.otpType,
          uniCode: this.uniCode || '',
        };
        const callbackData = await firstValueFrom(this.accountApi.pubsVerifyBindEmailForUpdate(param));
        this.onProcessEmailResponse(callbackData);
      } else if (this.sendType === 'BindMobile') {
        const param: VerifyBindMobileParams = {
          lotNumber: result.lot_number,
          captchaOutput: result.captcha_output,
          passToken: result.pass_token,
          genTime: result.gen_time,
          areaCode: this.areaCode,
          mobile: this.mobile,
          smsVoice: false,
          otpType: this.otpType,
        };
        const callbackData = await firstValueFrom(this.accountApi.pubsVerifyBindMobile(param));
        this.onProcessEmailResponse(callbackData);
      } else if (this.sendType === 'Mobile') {
        const param: SendMobileVerifyParams = {
          lotNumber: result.lot_number,
          captchaOutput: result.captcha_output,
          passToken: result.pass_token,
          genTime: result.gen_time,
          areaCode: this.areaCode,
          mobile: this.mobile,
          smsVoice: false,
          otpType: this.otpType,
        };
        const callbackData = await firstValueFrom(this.authApi.sendMobileVerify(param));
        this.onProcessEmailResponse(callbackData);
      } else if (this.sendType === 'AuthMobile') {
        const param: QuickSendMobileVerifyParams = {
          lotNumber: result.lot_number,
          captchaOutput: result.captcha_output,
          passToken: result.pass_token,
          genTime: result.gen_time,
          otpType: this.otpType,
        };
        const callbackData = await firstValueFrom(this.authApi.quickSendMobileVerify(param));
        this.onProcessEmailResponse(callbackData);
      } else if (this.sendType === 'UpdateMobile') {
        const param: VerifyBindMobileForUpdateParams = {
          lotNumber: result.lot_number,
          captchaOutput: result.captcha_output,
          passToken: result.pass_token,
          genTime: result.gen_time,
          areaCode: this.areaCode,
          mobile: this.mobile,
          smsVoice: false,
          otpType: this.otpType,
          uniCode: this.uniCode || '',
        };
        const callbackData = await firstValueFrom(this.accountApi.pubsVerifyBindMobileForUpdate(param));
        this.onProcessEmailResponse(callbackData);
      } else if (this.sendType === 'AuthGoogle') {
        // 如果是 Google 2FA, 不需要發送驗證碼
        this.isSend = false;
        this.isCountingDown = false;
        this.countdown = countdownTime;
      }
    } catch (error) {
      this.handleSend2faError(error);
    }
  }

  /**
   * 统一处理邮件返回值
   *
   * @param callbackData
   */
  onProcessEmailResponse(callbackData: ResponseData<boolean | string>) {
    if (callbackData.success) {
      const s = {
        isSend: true,
        unicode: callbackData?.data, // 这个uniCode 只有在绑定邮箱时候才会用到
      };
      this.sendOtpResponse.emit(s);
      this.setCountdown();
      this.toast.show({ message: 'OTP sent successfully', type: 'success' });
    } else {
      if (this.tryFallbackToEmail()) return;
      //发送验证码失败
      this.toast.show({ message: callbackData.message, type: 'fail' });
      this.resetCountdown();
    }
  }

  private handleSend2faError(error: unknown): void {
    console.error(error);
    if (this.tryFallbackToEmail()) return;

    const message =
      (error as { error?: { message?: string }; message?: string })?.error?.message ||
      (error as { message?: string })?.message ||
      'OTP sent failed';
    this.toast.show({ message, type: 'fail' });
    this.resetCountdown();
  }

  private tryFallbackToEmail(): boolean {
    const canFallbackToEmail =
      this.isSpecificType &&
      this.otpType === 'Login' &&
      this.sendType === 'Mobile' &&
      !!this.email &&
      !this.hasFallbackToEmail;

    if (!canFallbackToEmail) return false;

    this.hasFallbackToEmail = true;
    this.sendType = 'Email';
    this.verifyType = 'EMAIL';
    this.sendTypeChange.emit(this.sendType);
    this.resetCountdown();
    this.send2fa();
    return true;
  }

  setCountdown() {
    this.isSend = true;
    this.isCountingDown = true;
    this.startCountdown();
  }

  resetCountdown() {
    clearInterval(this.countdownInterval);
    this.isCountingDown = false;
    this.countdown = countdownTime;
    this.isSend = false;
  }

  startCountdown() {
    this.countdown = countdownTime;
    this.countdownInterval = setInterval(() => {
      this.countdown--;
      if (this.countdown <= 0) {
        clearInterval(this.countdownInterval);
        this.isCountingDown = false;
        this.isSend = false;
      }
    }, 1000);
  }

  get hasError() {
    return this.errorCodes.every((code, index) => code === this.codes[index]);
  }

  get allFieldsFilled() {
    return this.codes.every(code => code.length === 1);
  }

  async submitVerifyType() {
    const params: {
      verifyAction: VerifyAction;
      otpCode: string;
      googleCode: string;
      emailCode: string;
    } = {
      verifyAction: this.otpType,
      otpCode: '',
      googleCode: '',
      emailCode: '',
    };

    switch (this.verifyType) {
      case 'EMAIL':
        params.emailCode = this.codes.join('');
        break;
      case 'GOOGLE':
        params.googleCode = this.codes.join('');
        break;
      case 'MOBILE':
        params.otpCode = this.codes.join('');
        break;
      default:
        break;
    }

    return await firstValueFrom(this.authApi.quickGeneral2faVerify(params));
  }

  async submit() {
    // 1) 防連點：若正在送出，直接擋掉
    if (this.loading) return;
    // 2) 有錯先回家，不要進入送出流程
    if (this.hasError) {
      this.errorCodes = [...this.codes]; // 視需求：可同步標紅
      return;
    }
    this.isDirty = true;
    this.loading = true;
    try {
      let result = false;
      if (this.submitFunc) {
        result = (await this.submitFunc(this.codes.join(''), this.verifyType)) || false;
      } else {
        const verify = await this.submitVerifyType();
        if (!verify) {
          this.toast.show({ message: '2FA authentication failed.', type: 'fail' });
          this.loading = false;
          return;
        }
        if (verify.success) {
          result = (await this.verifyFunc?.(verify)) || false;
        } else {
          this.toast.show({
            message: verify.message || '2FA authentication failed.',
            type: 'fail',
          });
          this.loading = false;
          return;
        }
      }
      if (!result) {
        this.errorCodes = [...this.codes];
      }
    } catch (error) {
      console.error(error);
    }
    this.loading = false;
  }

  goToBind2fa() {
    this.router.navigate([this.appService.languageCode, 'settings', 'security'], {
      queryParams: {},
      fragment: undefined,
    });
    this.close();
  }

  close(): void {
    this.dialogRef.close();
  }
}
